<template>
  <div v-if="report.length === 0 && loading === false">
    No data available
  </div>
  <b-table
    v-else
    class="m-0"
    head-variant="light"
    thead-class="small"
    thead-tr-class="small text-uppercase"
    tbody-class="text-black"
    hover
    v-model="currentlyViewedReport"
    :items="report"
    :fields="tableFields"
    :sort-by.sync="sortBy"
    :per-page="perPage"
    :current-page="currentPage"
    :busy="busy"
    show-empty>
    <template v-slot:table-busy>
      <div class="text-center text-danger my-2">
        <b-spinner class="align-middle" variant="primary"></b-spinner>
        <strong>Loading...</strong>
      </div>
    </template>
    <template v-if="hasLegend" slot="hideShowButton" slot-scope="data">
      <b-button size="sm" class="rounded mx-3 iconButton" variant="outline-light"
                no-body
                @click="toggleDisabled(data.item)">
        <span
          :class="{ 'button-muted': data.item.disabled }"
          :style="{ color: graphColors[String(getEntryName(data.item, fields))] }"
          class="icon icon-Reports"/>
      </b-button>
    </template>
    <template v-for="(text, field) in fieldOptions" :slot="field" slot-scope="data">
      <slot :name="field" v-bind="data">
        <span
          :class="{ field: showChevrons, 'lastField': field === fields[fields.length - 1] }">
          <span class="fieldSizer">

            <template v-if="data.value">{{ data.value }}</template>
            <template v-else>Unspecified</template>
          </span>
        </span>
      </slot>
    </template>
    <template :slot="'cell(date)'" slot-scope="data">
      <span :class="{ field: showChevrons, 'lastField': 'date' === fields[fields.length - 1] }">
        {{ getFormattedDate(data.value, interval, timezone) }}
      </span>
    </template>
    <template v-for="(text, stat) in statOptions" :slot="'cell('+stat+')'" slot-scope="data" v-key="stat">

      <slot :name="stat" v-bind="data">
        <span v-b-tooltip.hover :title="data.value" class="stat text-muted">
          {{ formatStat(stat, data.value) }}
        </span>
      </slot>
    </template>
    <template v-for="slot in slots" :slot="'cell(' + slot.key?slot.key:slot + ')'" slot-scope="data">
      <slot :name="slot.key ? slot.key : slot" v-bind="data"/>
    </template>
    <template v-for="preSlot in preSlots" :slot="'cell(' + preSlot.key?preSlot.key:preSlot + ')'" slot-scope="data">
      <slot :name="preSlot.key ? preSlot.key : preSlot" v-bind="data"/>
    </template>
  </b-table>
</template>

<style scoped>
  .iconButton {
    padding-top: 4px !important;
    padding-right: 6px !important;
    padding-left: 6px !important;
  }

  .button-muted {
    color: var(--border) !important;
  }

  /* make fields not too big */
  .fieldSizer {
    display: table-cell;
    max-width: 300px;
    min-height: 24px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .fieldSizer:hover {
    white-space: normal;
  }

  /* stuff to show > between fields */
  .field {
    display: flex;
    font-size: 14px;
  }

  .stat {
    font-size: 13px;
  }

  td .field::after {
    flex-grow: 100;
    text-align: center;
    content: '\E914';
    font-family: 'icomoon';
    color: var(--gray);
    display: inline;
  }

  td .field.lastField:after {
    content: '';
  }

</style>
<style>
  .reportTable table > tbody > tr > td {
    white-space: nowrap;
  }

  th.buttonTh {
    width: 3em;
  }
</style>


<script>
    import {
        getPrettyFieldsList,
        getFieldOptions,
        getStatOptions,
        getPrettyStatName,
        formatStat,
        getEntryName,
        getFormattedDate,
        getReportCSV
    } from '@/reports/components/ReportUtils'
    import moment from 'moment'
    import TableSkeleton from '@/reports/components/TableSkeleton'

    export default {
        name: 'ReportTable',
        props: {
            busy: {required: false, type: Boolean, default: false},
            title: {required: false, type: String},
            report: {required: true, type: Array},
            stats: {required: true, type: Array},
            fields: {required: true, type: Array},
            interval: {required: false, type: String},
            graphColors: {required: false, type: Object},
            timezone: {required: false, type: String},
            currentPage: {required: false, type: Number},
            perPage: {required: false, type: Number, default: 10},
            //slots holds any custom fields that should be shown on the table
            preSlots: {
                required: false, type: Array, default: () => {
                    return []
                }
            },
            slots: {
                required: false, type: Array, default: () => {
                    return []
                }
            },
            //syncedWithGraph should be true iff there's a graph on the page showing
            //exactly what ReportTable is showing
            syncedWithGraph: {required: false, type: Boolean, default: false},
            //whether or not to show a chevron between fields
            showChevrons: {type: Boolean, default: true},
            loading: {type: Boolean, default: true}
        },
        data() {
            return {
                currentlyViewedReport: [],
                fieldOptions: getFieldOptions(),
                statOptions: getStatOptions(),
                currentRange: [],
                sortBy: ''
            }
        },
        computed: {
            hasLegend() {
                if (this.graphColors) {
                    return Object.keys(this.graphColors).length !== 0
                } else {
                    return false
                }
            },
            tableFields() {
                let newList = []
                for (let item of this.preSlots) {
                    newList.push({
                        key: item.key ? item.key : item,
                        sortable: false,
                        label: item.label ? item.label : item
                    })
                }
                if (this.hasLegend) {
                    newList.push({key: 'hideShowButton', label: '', thClass: 'buttonTh', tdClass: 'p-0'})
                }
                for (let item of this.fields) {
                    if (item === this.fields[this.fields.length - 1]) {
                        //is last field
                        newList.push({
                            key: item,
                            sortable: true,
                            label: this.fieldOptions[item],
                            tdClass: 'lastReportField'
                        })
                    } else {
                        newList.push({
                            key: item,
                            sortable: true,
                            label: this.fieldOptions[item]
                        })
                    }
                }
                /*
                 * Dummy columns for spacing between fields and stats
                 * Spacing table columns with css is fraught as heck
                 */
                newList.push({key: 'spacer_a', label: ''})
                newList.push({key: 'spacer_b', label: ''})
                newList.push({key: 'spacer_c', label: ''})
                for (let item of this.stats) {
                    newList.push({
                        key: item,
                        sortable: true,
                        label: getPrettyStatName(item),
                    })
                }
                for (let item of this.slots) {
                    newList.push({
                        key: item.key ? item.key : item,
                        sortable: false,
                        label: item.label ? item.label : item
                    })
                }
                return newList
            },
            processedCurrentlyViewedReport() {
                if (this.syncedWithGraph) {
                    for (let row of this.currentlyViewedReport) {
                        if (!('disabled' in row)) {
                            this.$set(row, 'disabled', false)
                        }
                    }
                }
                return this.currentlyViewedReport
            },
        },
        methods: {
            formatStat,
            getPrettyFieldsList,
            getEntryName,
            getFormattedDate,
            reset() {
                this.sortBy = ''
            },
            toggleDisabled(item) {
                let currentValue = item.disabled
                this.$set(item, 'disabled', !currentValue)
            },
            getRowIndex(pageIndex) {
                return pageIndex + this.currentRange[0]
            },
            mode() {
                return this.$route.path.split("/")[1]
            },
            downloadCSV() {
                let csvContent = getReportCSV(this.mode(), this.report, this.fields, this.stats, this.interval, this.timezone)
                let encodedUri = encodeURI(csvContent)
                window.open(encodedUri)
            }
        },
        watch: {
            currentlyViewedReport: function () {
                this.$emit('page-change', this.processedCurrentlyViewedReport)
            }
        },
        components: {
            TableSkeleton
        }
    }
</script>



// WEBPACK FOOTER //
// src/reports/components/ReportTable.vue